/* eslint-disable operator-linebreak */
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';
import { toJS } from 'mobx';
import * as yup from 'yup';
import { FORM_ERROR, FormState } from 'final-form';
import { FormSpy } from 'react-final-form';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import Loader from 'ui/Loader/Loader';
import { OTPVerificationModal, StatusModal } from 'components/modals';
import { requiredOnly, validateAmount, validateAccount, validatePhone } from 'utils/validation';
import { mapYupObjectError, objectMap } from 'utils/helpers';
import { changeFieldValue } from 'utils/formMutators';
import accountsStore from 'store/accountsStore';
import api from 'api/api';
import { AxiosStatus } from 'api/axiosConfig';
import routePaths from 'routes/routePaths';
import { NETWORK_REQUIRED } from 'utils/constants';
import { IApiResponse } from 'utils/models';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import { FormType as FormTypeOTP } from 'components/modals/OTPVerificationModal/OTPVerificationModal';
import renderFields from './FxTransferRequestFields';
import { IBanksResponse, IFormValues, IFXState } from './FxTransferRequest.model';
import ON_SUBMITS from './submitApi';

interface IProps {
  handleChangeForm: () => void;
}

const schema = yup.object().shape({
  transferType: requiredOnly('Transfer Type'),
  currency: requiredOnly('Transaction Currency'),
  sourceAccount: requiredOnly('Account to Debit'),
  amount: validateAmount(),
  beneficiaryName: requiredOnly('Beneficiary Name'),
  beneficiaryAccount: validateAccount('Beneficiary Account'),
  beneficiaryCountry: requiredOnly('Beneficiary Country'),
  beneficiaryBankName: requiredOnly('Beneficiary Bank Name'),
  beneficiaryBankAddress: requiredOnly('Beneficiary Bank Address'),
  swiftCode: requiredOnly('Swift Code'),
  sortCode: requiredOnly('Sort Code'),
  routingNumber: yup.string().notRequired(),
  senderTelNumber: validatePhone('Contact Phone Number'),
  feePaidBy: requiredOnly('Fees to Be Paid By'),
  accountForCharges: yup.string().when('feesToBePaidBy', {
    is: 'Sender',
    then: validateAccount('Account for Fees'),
  }),
  purpose: requiredOnly('Purpose of Transfer'),
});

const FxTransferRequest = ({ handleChangeForm }: IProps) => {
  const [state, setState] = useState<IFXState>({ accounts: [], banks: [] });
  const [isOTPModalOpen, setOTPModalOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [OTPModalError, setOTPModalError] = useState<IOTPError | null>(null);
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const [loadDataStatus, setLoadDataStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;

  const loadingData = loadDataStatus === AxiosStatus.PENDING;

  const { accounts: storeAccounts } = accountsStore;

  const updateState = useCallback(
    (update: Partial<IFXState>) => setState((current) => ({ ...current, ...update })),
    [],
  );

  const history = useHistory();
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;
  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };

  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
    setSuccessMsg('');
    setErrorMsg('');
  };
  useEffect(() => updateState({ accounts: toJS(storeAccounts) }), [storeAccounts, updateState]);

  useEffect(() => {
    const fetchAccounts = () => {
      if (storeAccounts.length) {
        // accounts loaded already
        return Promise.resolve();
      }
      return accountsStore.fetchAccounts();
    };

    const fetchBanks = () =>
      api
        .get('reference/banks', {}, true, false)
        .then((response: IBanksResponse) => {
          updateState({ banks: response.banks });
        })
        .catch((error) => {
          console.error('error', error);
        });

    setLoadDataStatus(AxiosStatus.PENDING);

    Promise.all([fetchBanks(), fetchAccounts()])
      .then(() => {
        setLoadDataStatus(AxiosStatus.SUCCESS);
      })
      .catch(() => {
        setLoadDataStatus(AxiosStatus.FAILURE);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const closeOTPModal = () => {
    setOTPModalOpen(false);
    setSubmitStatus(AxiosStatus.DEFAULT);
  };
  const successConfirmationText =
    'International transfer request received successfully. One of our advisors will be with you shortly';

  const validate = useCallback((values: IFormValues) => {
    try {
      schema.validateSync(values, { abortEarly: false });
    } catch (e) {
      const valErrs = mapYupObjectError<IFormValues>(e);
      return objectMap(valErrs, (v: string[]) => v[0]);
    }
    return undefined;
  }, []);

  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || 'FX Transfer is unsuccessful! Please try again later.');
    setSubmitStatus(AxiosStatus.FAILURE);
    setOTPModalOpen(false);
  }, []);

  const onSubmit = (values: IFormValues) => {
    setSubmitStatus(AxiosStatus.PENDING);
    const account = storeAccounts.find((a) => a.accountNumber === values.sourceAccount);

    if (Number(account?.availableBalance) < Number(values.amount)) {
      setSubmitStatus(AxiosStatus.FAILURE);
      setErrorMsg('Transfer failed.\nInsufficient funds to complete the transaction');
      return;
    }
    api
      .post('/customer/otp/generate')
      .then(() => {
        setOTPModalOpen(true);
      })
      .catch((data) => {
        onRejectedSubmit(data);
      });
  };

  const onOtpSubmit = (values: IFormValues) => (otp: string | undefined, form: FormTypeOTP | undefined) =>
    api
      .post(`customer/otp/verify/${otp || ''}/false/${String(sessionStorage.getItem('userName'))}`)
      .then(() => {
        ON_SUBMITS(values, String(otp))
          .then((data: IApiResponse) => {
            setSuccessMsg(data.description || successConfirmationText);
            setSubmitStatus(AxiosStatus.SUCCESS);
          })
          .catch((error: IApiResponse) => {
            setErrorMsg(error.description);
            setSubmitStatus(AxiosStatus.FAILURE);
          })
          .finally(() => setOTPModalOpen(false));
      })
      .catch((error: IOTPError) => {
        setOTPModalError(error);
        if (error.failureCount === 0 && form) {
          form.initialize({ digit1: '', digit2: '', digit3: '', digit4: '', digit5: '' });
        }
        return { [FORM_ERROR]: error.description || NETWORK_REQUIRED };
      });

  const onResend = () => api.post('/customer/otp/generate');

  const onChangeForm = ({ dirty }: FormState<IFormValues>) => {
    if (dirty) {
      handleChangeForm();
    }
  };

  return (
    <>
      {loadingData && <Loader />}
      {!loadingData && (
        <Form
          onSubmit={onSubmit}
          validate={validate}
          mutators={{ changeFieldValue }}
          render={({ handleSubmit, hasValidationErrors, values, form }) => (
            <form onSubmit={handleSubmit}>
              {renderFields(state, values, form, submitting)}
              <SubmitLeveler>
                <Button type="submit" fullWidth disabled={submitting || hasValidationErrors}>
                  {submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit Request'}
                </Button>
              </SubmitLeveler>
              <OTPVerificationModal
                open={isOTPModalOpen}
                onClose={closeOTPModal}
                onSubmit={onOtpSubmit(values)}
                onResend={onResend}
                OTPModalError={OTPModalError}
              />
              <FormSpy subscription={{ dirty: true }} onChange={onChangeForm} />
            </form>
          )}
        />
      )}
      <StatusModal
        open={showStatusModal}
        status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
        onClose={onCloseStatusModal}
        onRetry={onCloseStatusModal}
        onRetryText="Close"
      >
        {submitStatus === AxiosStatus.SUCCESS ? successMsg : (
          <div style={{ whiteSpace: 'pre-line' }}>{errorMsg}</div>
        )}
      </StatusModal>
    </>
  );
};

export default FxTransferRequest;
